<template>
  <b-card no-body class="section-card">
    <div class="section-head">
      <span class="section-letter section-letter-icon">
        <feather-icon icon="MessageSquareIcon" size="16" />
      </span>
      <div>
        <h4 class="mb-0">Requests to Swiss Finance</h4>
        <small class="text-muted">Ask us for a document you need (e.g. a signed copy, a confirmation)</small>
      </div>
    </div>
    <b-card-body>
      <p v-if="!customerRequests.length" class="text-muted">You have not requested any documents yet.</p>

      <div v-for="request in customerRequests" :key="'cr' + request.id" class="request-row">
        <div class="request-main">
          <div>
            <strong>{{ request.Title }}</strong>
            <b-badge :variant="'light-' + variant(request.Status)" class="ml-50">{{ request.Status }}</b-badge>
            <small v-if="request.created_at" class="text-muted ml-50">{{ formatDate(request.created_at) }}</small>
          </div>
          <small v-if="request.Note" class="text-muted d-block">{{ request.Note }}</small>
          <div v-if="request.Status === 'Declined' && request.DeclineReason" class="requirement-reject mt-25">
            <feather-icon icon="AlertCircleIcon" size="14" class="mr-25" />
            <span><strong>Declined:</strong> {{ request.DeclineReason }}</span>
          </div>
          <div v-if="request.Status === 'Fulfilled' && request.FulfilledDocumentId" class="requirement-file mt-25">
            <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />
            <a href="#" @click.prevent="download(request)">{{ request.FulfilledFileName || "Download document" }}</a>
            <span v-if="request.FulfilledAt" class="text-muted">· {{ formatDate(request.FulfilledAt) }}</span>
          </div>
        </div>
      </div>

      <hr />

      <h6 class="mt-0">New request</h6>
      <validation-observer ref="requestObserver">
        <b-form @submit.prevent="submit">
          <b-row>
            <b-col cols="12" md="5">
              <b-form-group label="What do you need? *">
                <validation-provider #default="{ errors }" rules="required" name="Title">
                  <b-form-input v-model="title" placeholder="e.g. Countersigned agreement" :state="errors.length ? false : null" :disabled="sending" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="7">
              <b-form-group label="Note (optional)">
                <b-form-input v-model="note" placeholder="Any details that help us" :disabled="sending" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-button type="submit" variant="outline-primary" size="sm" :disabled="sending">
            <b-spinner v-if="sending" small class="mr-25" />
            <feather-icon v-else icon="SendIcon" size="14" class="mr-25" />
            Send request
          </b-button>
        </b-form>
      </validation-observer>
    </b-card-body>
  </b-card>
</template>

<script>
// Requests from the client to SFC (title + note) and their status / fulfilled file.
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import { requestDocument, downloadDocument, errorMessage, formatDate, customerRequestStatusVariant } from "./publicApi";

export default {
  name: "CustomerRequestsCard",

  components: { ValidationObserver, ValidationProvider },

  props: {
    customerRequests: { type: Array, default: () => [] },
  },

  data() {
    return {
      required,
      title: "",
      note: "",
      sending: false,
    };
  },

  methods: {
    formatDate,

    variant(status) {
      return customerRequestStatusVariant[status] || "secondary";
    },

    submit() {
      this.$refs.requestObserver.validate().then((valid) => {
        if (!valid) return;
        this.sending = true;
        requestDocument({ title: this.title.trim(), note: this.note.trim() || null })
          .then((res) => {
            this.$bvToast.toast(res.data.message || "Your request has been sent.", {
              title: "Request sent",
              variant: "success",
              solid: true,
            });
            this.title = "";
            this.note = "";
            this.$nextTick(() => this.$refs.requestObserver && this.$refs.requestObserver.reset());
            this.$emit("payload", res.data);
          })
          .catch((err) => {
            this.$bvToast.toast(errorMessage(err, "The request could not be sent."), {
              title: "Error",
              variant: "danger",
              solid: true,
            });
          })
          .finally(() => {
            this.sending = false;
          });
      });
    },

    download(request) {
      downloadDocument(request.FulfilledDocumentId, request.FulfilledFileName).catch(() => {
        this.$bvToast.toast("Download failed.", { title: "Error", variant: "danger", solid: true });
      });
    },
  },
};
</script>
